import React from 'react';
import { useObjectRef } from 'react-aria';
import { Group, Input as AriaInput, TextField as AriaTextField } from 'react-aria-components';

import { FieldActionContext } from '../field/field-action';
import {
  adornmentStyles,
  entryStyles,
  FieldLabel,
  FieldMessage,
  focusEntryOnPress,
  frameState,
  frameStyles,
  resolveFieldState,
  useFieldLayout,
  useFieldMessageIds,
} from '../field/field-shell';

interface TextFieldCommonProps {
  /**
   * Help under the field: the rule the value must meet, or why the field is
   * read-only. Inline content only (Text, Link, Code runs), never blocks.
   * Hidden while `error` shows, since the error restates the rule.
   */
  description?: React.ReactNode;
  /**
   * The error message. The field is invalid exactly when this is non-empty.
   * Say what to do, restating the rule: "Enter a URL that starts with
   * https://", never "Invalid value".
   */
  error?: string;
  /** Marks the label with an asterisk and exposes `aria-required`. */
  isRequired?: boolean;
  /**
   * Washed out and skipped by Tab, its action with it. Prefer `isReadOnly`
   * whenever the value still matters to the reader.
   */
  isDisabled?: boolean;
  /** Full contrast and focusable, but not editable. Say why in `description`. */
  isReadOnly?: boolean;
  /** The value (controlled). */
  value?: string;
  /** The initial value (uncontrolled). */
  defaultValue?: string;
  /** Called with the new value on every change. */
  onChange?: (value: string) => void;
  /** Called when the input loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when the input receives focus. */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Keys pressed in the input. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Example input, never a substitute for the label. */
  placeholder?: string;
  /**
   * The native input type. Search has its own control (SearchField), and a
   * date picker is a later component; `date` passes through meanwhile. The
   * browser's own checks for a type run only in a form without `noValidate`
   * (field.md).
   */
  type?: 'text' | 'email' | 'url' | 'tel' | 'password' | 'number' | 'date';
  /** The form field name. */
  name?: string;
  /** The id of the input element. */
  id?: string;
  /** Whether the input takes focus on mount. */
  autoFocus?: boolean;
  /** The browser autofill hint. */
  autoComplete?: string;
  /** The virtual keyboard hint on touch devices. */
  inputMode?: 'text' | 'email' | 'url' | 'tel' | 'numeric' | 'decimal' | 'search' | 'none';
  /** The native lower limit of a date or number, checked as `type` is. */
  min?: string | number;
  /** The native upper limit of a date or number, checked as `type` is. */
  max?: string | number;
  /** A decorative glyph at the start: a heroicons 24/outline component. */
  iconStart?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** A decorative glyph at the end, before any action. */
  iconEnd?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Fixed text before the value, inside the frame ("https://", "@"). */
  prefix?: string;
  /** Fixed text after the value, inside the frame ("minutes", "%"). */
  suffix?: string;
}

interface TextFieldLabelledProps {
  /** The visible name, above the field or beside it (Form decides). */
  label: string;
  'aria-label'?: never;
  'aria-labelledby'?: never;
}

interface TextFieldUnlabelledProps {
  label?: never;
  /** The accessible name of a field with no visible label (a table cell). */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface TextFieldLabelledByProps {
  label?: never;
  'aria-label'?: never;
  /** The id of the visible element that names the field (a question's text). */
  'aria-labelledby': string;
}

interface TextFieldSmallProps {
  /** Height on the Button ramp: sm 24px, md 32px, lg 40px. */
  size: 'sm';
  /** A 24px field seats no action: the smallest action is its own height. */
  action?: never;
}

interface TextFieldActionProps {
  /** Height on the Button ramp: sm 24px, md 32px, lg 40px. */
  size?: 'md' | 'lg';
  /**
   * One FieldAction at the end of the frame (a reveal, a copy). The field
   * sizes it and disables it with itself; nothing else belongs in the seat.
   */
  action?: React.ReactElement;
}

export type TextFieldProps = TextFieldCommonProps &
  (TextFieldLabelledProps | TextFieldUnlabelledProps | TextFieldLabelledByProps) &
  (TextFieldSmallProps | TextFieldActionProps);

/**
 * A single-line text input with its label, help and error. Flat props: the
 * field renders its own anatomy, and the enclosing Form decides whether the
 * label sits above or beside it (field.md).
 *
 * Validation is `aria`: React Hook Form (or any owner) decides when a value
 * is invalid and passes the message as `error`; the field never blocks
 * submission itself. The forwarded ref is the `<input>`, so a form library
 * focusing the first invalid field lands on the real input.
 */
export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    description,
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
    size = 'md',
    type = 'text',
    iconStart: IconStart,
    iconEnd: IconEnd,
    prefix,
    suffix,
    action,
    placeholder,
    min,
    max,
    onBlur,
    onFocus,
    onKeyDown,
    ...props
  },
  ref,
) {
  const { error, isRequired, isDisabled, isReadOnly, isInvalid } = resolveFieldState({
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
  });
  const inputRef = useObjectRef(ref);
  const { labelPosition, styles } = useFieldLayout(size);
  const { root, label: labelClass, column } = styles;
  const { icon, affix } = adornmentStyles({ size });
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, error);
  const seat = React.useMemo(
    () => ({ size: size === 'sm' ? ('md' as const) : size, isDisabled }),
    [size, isDisabled],
  );

  return (
    <AriaTextField
      {...props}
      type={type}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isInvalid={isInvalid}
      validationBehavior="aria"
      aria-describedby={describedBy}
      className={root()}
      data-component="text-field"
      data-label-position={labelPosition}
    >
      {label ? (
        <FieldLabel isRequired={isRequired} className={labelClass()}>
          {label}
        </FieldLabel>
      ) : null}
      <div className={column()}>
        <Group
          className={frameStyles({
            size,
            multiline: false,
            hasAction: !!action,
            state: frameState({ isDisabled, isReadOnly, isInvalid }),
          })}
          onMouseDown={focusEntryOnPress(inputRef)}
        >
          {IconStart ? <IconStart aria-hidden="true" className={icon()} /> : null}
          {prefix ? <span className={affix()}>{prefix}</span> : null}
          {/* On the Input, not the field: these are React DOM events for a
           * form library, and RAC's own field-level onKeyDown stops
           * propagation of every key, which would swallow a host's Escape. */}
          <AriaInput
            ref={inputRef}
            className={entryStyles({ size, multiline: false, readOnly: isReadOnly })}
            placeholder={placeholder}
            min={min}
            max={max}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
          />
          {suffix ? <span className={affix()}>{suffix}</span> : null}
          {IconEnd ? <IconEnd aria-hidden="true" className={icon()} /> : null}
          {action && size !== 'sm' ? (
            <FieldActionContext.Provider value={seat}>{action}</FieldActionContext.Provider>
          ) : null}
        </Group>
        <FieldMessage
          description={description}
          error={error}
          descriptionId={descriptionId}
          errorId={errorId}
        />
      </div>
    </AriaTextField>
  );
});
